import { useState, useEffect } from 'react';
import AppLayout from '../components/AppLayout';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { TrendingUp, Download, DollarSign, FileText } from 'lucide-react';
import type { Invoice, Finding } from '../../shared/types';
import { apiFetch } from '../lib/api';

export default function Analytics() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [findings, setFindings] = useState<Finding[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [invoicesData, findingsData] = await Promise.all([
          apiFetch<Invoice[]>('/api/invoices'),
          apiFetch<Finding[]>('/api/findings'),
        ]);

        setInvoices(invoicesData);
        setFindings(findingsData);
      } catch (error) {
        console.error('Failed to fetch analytics data:', error);
      }
    };

    fetchData();
  }, []);

  const savingsOverTime = findings.reduce((acc, finding) => {
    const date = new Date(finding.created_at).toLocaleDateString('en-US', { month: 'short' });
    const existing = acc.find((item) => item.month === date);
    
    if (existing) {
      existing.savings += finding.amount || 0;
    } else {
      acc.push({ month: date, savings: finding.amount || 0 });
    }
    
    return acc;
  }, [] as { month: string; savings: number }[]);

  const topOffenders = findings.reduce((acc, finding) => {
    const type = finding.finding_type || 'Unknown';
    const existing = acc.find((item) => item.type === type);
    
    if (existing) {
      existing.count += 1;
      existing.amount += finding.amount || 0;
    } else {
      acc.push({ type, count: 1, amount: finding.amount || 0 });
    }
    
    return acc;
  }, [] as { type: string; count: number; amount: number }[]).sort((a, b) => b.amount - a.amount).slice(0, 5);

  const totalSavings = findings.reduce((sum, f) => sum + (f.amount || 0), 0);
  const totalInvoices = invoices.length;
  const avgSavingsPerInvoice = totalInvoices > 0 ? totalSavings / totalInvoices : 0;

  const handleExport = () => {
    const csv = [
      ['Date', 'Vendor', 'Amount', 'Finding Type', 'Severity'],
      ...findings.map((f) => [
        f.created_at,
        'N/A',
        f.amount || 0,
        f.finding_type || 'Unknown',
        f.severity || 'N/A',
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vigilant-findings-export.csv';
    a.click();
  };

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Analytics & Reports</h1>
            <p className="text-slate-400">Track savings and detection performance</p>
          </div>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:from-cyan-600 hover:to-blue-700 transition-all"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border border-emerald-500/20 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
            <p className="text-sm text-slate-400 mb-1">Total Savings</p>
            <p className="text-3xl font-bold text-white">${totalSavings.toFixed(2)}</p>
          </div>

          <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-cyan-400" />
              </div>
            </div>
            <p className="text-sm text-slate-400 mb-1">Invoices Processed</p>
            <p className="text-3xl font-bold text-white">{totalInvoices}</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-400" />
              </div>
            </div>
            <p className="text-sm text-slate-400 mb-1">Avg Savings/Invoice</p>
            <p className="text-3xl font-bold text-white">${avgSavingsPerInvoice.toFixed(2)}</p>
          </div>
        </div>

        {/* Savings Over Time Chart */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Savings Over Time</h2>
          {savingsOverTime.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={savingsOverTime}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="month" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                />
                <Line type="monotone" dataKey="savings" stroke="#06b6d4" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center">
              <p className="text-slate-500">No data available</p>
            </div>
          )}
        </div>

        {/* Top Offenders Chart */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Top Finding Types</h2>
          {topOffenders.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topOffenders}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="type" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                />
                <Bar dataKey="amount" fill="#06b6d4" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center">
              <p className="text-slate-500">No data available</p>
            </div>
          )}
        </div>

        {/* Processing Stats */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Processing Statistics</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-slate-400 mb-1">Detection Rate</p>
              <p className="text-2xl font-bold text-white">
                {totalInvoices > 0 ? ((findings.length / totalInvoices) * 100).toFixed(1) : 0}%
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-1">High Severity</p>
              <p className="text-2xl font-bold text-red-400">
                {findings.filter((f) => f.severity === 'HIGH').length}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-1">Medium Severity</p>
              <p className="text-2xl font-bold text-orange-400">
                {findings.filter((f) => f.severity === 'MEDIUM').length}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-1">Low Severity</p>
              <p className="text-2xl font-bold text-yellow-400">
                {findings.filter((f) => f.severity === 'LOW').length}
              </p>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
